import React, { useState, useEffect } from 'react';
import qs from 'querystring';
import { useSelector } from 'react-redux';
import { useMutation, useApolloClient } from '@apollo/react-hooks';
import Helmet from 'react-helmet';
import Alert from '@material-ui/lab/Alert';
import Container from '@material-ui/core/Container';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import IconButton from '@material-ui/core/IconButton';
import useSnack from 'hooks/useSnack';
import set from 'lodash/set';
import Spin from 'components/common/Spin';
import {
  GET_EXERCISE,
  GET_STUDENT_EXERCISE_ID,
  SAVE_EXERCISE_RESPONSE,
} from 'graphql/student/worksheets';
import ExerciseSteps from './components/ExterciseSteps';
import withConfirm from 'hooks/withConfirm';
import { isEmpty, isEqual, xorWith, cloneDeep } from 'lodash';
import NavigationPrompt from 'react-router-navigation-prompt';
import CustomPrompt from 'components/common/CustomPrompt';
import { ROLES } from 'constant/userTypes';
import PRODUCTS from 'constant/product-types';

const PRODUCTS_LOOKUP = PRODUCTS.reduce((memo, i) => {
  memo[i.value] = i.label;
  return memo;
}, {});

const useStyles = makeStyles((theme) => ({
  root: {
    position: 'fixed',
    left: 0,
    top: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(255, 255, 255, .95)',
    backdropFilter: 'blur(8px)',
    paddingTop: 60,
    paddingBottom: 60,
    zIndex: 99,
  },
  title: {
    fontSize: 18,
    fontWeight: 500,
  },
  desc: {
    marginBottom: theme.spacing(3),
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  header: {
    '& button': {
      position: 'absolute',
      top: 10,
      right: 20,
    },
  },
  content: {
    overflow: 'auto',
    height: 'calc(100vh - 80px)',
    paddingRight: 16,
  },
}));

const ExerciseFrom = ({
  studentId,
  history,
  location,
  confirm,
  match: { params },
  from,
}) => {
  const { id } = params;
  const query = qs.parse(location.search.substr(1));
  const client = useApolloClient();
  const profile = useSelector((s) => s.student.profile);
  const classes = useStyles();
  const snack = useSnack();
  const [loading, setLoading] = useState(true);
  const [resp, setResp] = useState();
  const [respClone, setRespClone] = useState();
  const [formState, setFormState] = useState({ blocks: [] });
  const [saveResponse] = useMutation(SAVE_EXERCISE_RESPONSE);
  const isArrayEqual = (x, y) => isEmpty(xorWith(x, y, isEqual));
  const user = useSelector((s) => s.user.user);

  useEffect(() => {
    if (query.exercise) {
      client
        .query({
          query: GET_STUDENT_EXERCISE_ID,
          variables: { userId: studentId, exerciseId: query.exercise },
        })
        .then(({ data }) => {
          if (data.data.length) {
            history.replace(`/exercises/worksheet/${data.data[0].id}`);
          } else {
            snack.error('Exercise not found.');
            history.push('/exercises');
          }
        });
    } else {
      client
        .query({
          query: GET_EXERCISE,
          variables: { id },
          fetchPolicy: 'network-only',
        })
        .then(({ data }) => {
          setRespClone(cloneDeep(data.data[0]));
          setResp(data.data[0]);
          setLoading(false);
        })
        .catch((e) => {
          snack.error(e.message);
        });
    }
  }, [id]);

  useEffect(() => {
    if (resp) {
      const { exercise, response } = resp;
      if (!response) {
        setFormState({ blocks: exercise.blocks });
      } else {
        setFormState({ blocks: response });
      }
    }
  }, [resp]);

  const saveData = () => {
    const { status: milestone_status } = student_milestone;
    return saveResponse({
      variables: {
        id,
        payload: {
          response: formState.blocks,
          status: !status ? 'inprogress' : status,
        },
        milestoneId: milestone_id,
        input: {
          status: !milestone_status ? 'inprogress' : milestone_status,
        },
      },
    }).then(
      ({ data }) => {
        const saved = data.update_student_milestone_exercises.returning[0];
        setRespClone(
          cloneDeep({
            ...resp,
            ...saved,
          }),
        );
        setResp({
          ...resp,
          ...saved,
        });
        window.analytics.track('Exercise saved', {
          ...resp,
          ...saved,
          product: PRODUCTS_LOOKUP[profile.product_type],
        });
        snack.success('Response saved');
      },
      (e) => {
        snack.error(e.message);
      },
    );
  };

  const submitAnswer = () => {
    const { status: milestone_status } = student_milestone;

    confirm(() => {
      return saveResponse({
        variables: {
          id,
          payload: {
            response: formState.blocks,
            completed_at: 'now()',
            status: 'done',
          },
          milestoneId: milestone_id,
          input: {
            status: !milestone_status ? 'inprogress' : milestone_status,
          },
        },
      }).then(
        ({ data }) => {
          const saved = data.update_student_milestone_exercises.returning[0];
          setResp({
            ...resp,
            ...saved,
          });
          window.analytics.track('Exercise submitted', {
            ...resp,
            ...saved,
            product: PRODUCTS_LOOKUP[profile.product_type],
          });
          snack.success('Answer submitted.');
        },
        (e) => snack.error(e.message),
      );
    });
  };

  const handleNavigation = () => {
    if (from === 'library') {
      return history.goBack();
    } else if (location?.state?.currentIndex) {
      return history.replace({ pathname: routePath, state: location?.state });
    } else {
      return history.goBack();
    }
  };

  const onChange = (key, value) => {
    set(formState, key, value);
    setFormState({ ...formState });
  };

  if (loading) {
    return <Spin spinning linear />;
  }

  if (!resp) {
    return 'Exercise not found.';
  }

  const { exercise, status, milestone_id, student_milestone } = resp;

  const routePath =
    user.type === ROLES.STUDENT
      ? `/timeline`
      : `/students/${studentId}/timeline`;

  return (
    <div className={classes.root}>
      <Helmet title={exercise.name} />
      <div className={classes.header}>
        <IconButton onClick={handleNavigation}>
          <CloseIcon fontSize={'large'} />
        </IconButton>
      </div>
      <Container maxWidth="md">
        {profile.is_trial && (
          <Alert color="warning" style={{ marginBottom: 16, fontWeight: 500 }}>
            You cannot submit exercise as you're on trial plan.
          </Alert>
        )}
        <Grid container alignItems="center">
          <Grid item xs={12} sm={12}>
            <div className={classes.content}>
              <Typography color="textSecondary" variant="caption">
                {exercise.module.name} ~ {exercise.duration} minutes
              </Typography>
              <Typography
                gutterBottom
                variant="subtitle1"
                color="textPrimary"
                className={classes.title}
              >
                {exercise.name}
              </Typography>
              <Typography
                variant="body2"
                color="textSecondary"
                className={classes.desc}
              >
                {exercise.description}
              </Typography>
              <ExerciseSteps
                hideActions={profile.is_trial}
                status={status}
                blocks={formState.blocks}
                onChange={onChange}
                onSave={saveData}
                onSubmit={submitAnswer}
              />
            </div>
          </Grid>
        </Grid>
      </Container>
      <NavigationPrompt
        when={!isArrayEqual(respClone?.response, formState?.blocks)}
      >
        {({ onConfirm, onCancel }) => (
          <CustomPrompt
            when={true}
            message={JSON.stringify({
              title: 'Are you sure you want to leave?',
              description:
                'Make sure you save any changes first, so you don’t lose what you’ve typed.',
              confirmationText: "Don't Save",
              confirmButtonProps: {
                color: 'secondary',
              },
            })}
            onCancel={onCancel}
            onConfirm={onConfirm}
          />
        )}
      </NavigationPrompt>
      ;
    </div>
  );
};

export default withConfirm(ExerciseFrom);
